/**
 * Navigation Hooks
 *
 * Type-safe navigation hooks for React Router with context preservation and return path handling.
 * Provides a unified interface for programmatic navigation throughout the application.
 *
 * @module shared/hooks/routing/useNavigation
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';
import type { RouteParams, QueryParams, NavigationContext } from '@shared/types/routing';
import { buildUrl } from '@shared/utils/routing/urlBuilder';

/**
 * Type-safe navigation helper
 *
 * Provides methods for programmatic navigation with type safety and context preservation.
 *
 * @returns Navigation helper object with typed navigation methods
 *
 * @example
 * ```typescript
 * function OrganizationList() {
 *   const nav = useTypedNavigation();
 *
 *   const handleOrganizationClick = (id: number) => {
 *     nav.navigateTo('/organizations/:id', { id }, { page: 1 }, {
 *       state: { returnPath: '/organizations' }
 *     });
 *   };
 *
 *   const handleBack = () => {
 *     nav.goToReturnPath('/organizations');
 *   };
 * }
 * ```
 */
export function useTypedNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigate to a route with path parameters, query parameters, and context
   *
   * @param route - Route pattern (e.g., '/organizations/:id')
   * @param params - Path parameters to replace placeholders
   * @param query - Query parameters to append
   * @param context - Navigation context (replace, state, hash, etc.)
   *
   * @example
   * ```typescript
   * // Navigate to organization detail
   * navigateTo('/organizations/:id', { id: 123 });
   *
   * // Navigate with query parameters
   * navigateTo('/campaigns', undefined, { status: 'active', page: 2 });
   *
   * // Navigate with state
   * navigateTo('/routes/:id', { id: 'abc' }, undefined, {
   *   state: { returnPath: '/routes', scrollY: 450 }
   * });
   * ```
   */
  const navigateTo = useCallback(
    (
      route: string,
      params?: RouteParams,
      query?: QueryParams,
      context?: NavigationContext
    ) => {
      const {
        replace = false,
        state,
        hash,
        preventScrollReset = false,
        meta,
        returnPath,
      } = context || {};

      // Build complete URL
      const url = buildUrl(route, params, query, hash);

      // Merge state with return path if provided
      const mergedState = {
        ...state,
        ...(returnPath ? { returnPath } : {}),
        ...(meta ? { meta } : {}),
      };

      // Navigate with React Router
      navigate(url, {
        replace,
        state: Object.keys(mergedState).length > 0 ? mergedState : undefined,
        preventScrollReset,
      });
    },
    [navigate]
  );

  /**
   * Navigate back to previous route
   *
   * Uses browser's back button navigation. If no history exists,
   * navigates to fallback route.
   *
   * @param fallback - Optional fallback route if no history exists
   *
   * @example
   * ```typescript
   * // Go back with fallback
   * goBack('/campaigns');
   *
   * // Go back without fallback (stays on current page if no history)
   * goBack();
   * ```
   */
  const goBack = useCallback(
    (fallback?: string) => {
      // Check if we have history to go back to
      if (window.history.length > 1) {
        navigate(-1);
      } else if (fallback) {
        navigate(fallback);
      }
      // If no history and no fallback, do nothing
    },
    [navigate]
  );

  /**
   * Navigate to return path stored in location state
   *
   * Useful for "back" buttons that should return to a specific view.
   * If no return path is found in state, navigates to fallback.
   *
   * @param fallback - Fallback route if no return path in state
   * @param preserveQuery - Whether to preserve current query parameters (default: false)
   *
   * @example
   * ```typescript
   * // Organization detail view with back button
   * function OrganizationDetail() {
   *   const nav = useTypedNavigation();
   *
   *   return (
   *     <button onClick={() => nav.goToReturnPath('/organizations')}>
   *       Back to List
   *     </button>
   *   );
   * }
   * ```
   */
  const goToReturnPath = useCallback(
    (fallback: string, preserveQuery = false) => {
      const returnPath = (location.state as any)?.returnPath;

      if (returnPath) {
        if (preserveQuery && location.search) {
          navigate(`${returnPath}${location.search}`);
        } else {
          navigate(returnPath);
        }
      } else {
        navigate(fallback);
      }
    },
    [location, navigate]
  );

  /**
   * Update current route's query parameters without navigation
   *
   * Useful for updating filters without full page navigation.
   *
   * @param query - New query parameters to merge with current
   * @param replace - Whether to replace current history entry (default: true)
   *
   * @example
   * ```typescript
   * // Update page number
   * updateQuery({ page: 2 });
   *
   * // Update multiple filters
   * updateQuery({ status: 'active', sort: 'name' });
   * ```
   */
  const updateQuery = useCallback(
    (query: QueryParams, replace = true) => {
      const searchParams = new URLSearchParams(location.search);

      // Merge new query params
      for (const [key, value] of Object.entries(query)) {
        if (value === null || value === undefined) {
          searchParams.delete(key);
        } else if (Array.isArray(value)) {
          searchParams.delete(key);
          value.forEach(v => searchParams.append(key, String(v)));
        } else {
          searchParams.set(key, String(value));
        }
      }

      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash}`;

      navigate(newUrl, { replace, state: location.state });
    },
    [location, navigate]
  );

  /**
   * Clear specific query parameters from current route
   *
   * @param keys - Array of query parameter keys to remove
   * @param replace - Whether to replace current history entry (default: true)
   *
   * @example
   * ```typescript
   * // Clear search and page filters
   * clearQuery(['search', 'page']);
   * ```
   */
  const clearQuery = useCallback(
    (keys: string[], replace = true) => {
      const searchParams = new URLSearchParams(location.search);

      keys.forEach(key => searchParams.delete(key));

      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash}`;

      navigate(newUrl, { replace, state: location.state });
    },
    [location, navigate]
  );

  /**
   * Clear all query parameters from current route
   *
   * @param replace - Whether to replace current history entry (default: true)
   *
   * @example
   * ```typescript
   * // Reset all filters
   * clearAllQuery();
   * ```
   */
  const clearAllQuery = useCallback(
    (replace = true) => {
      const newUrl = `${location.pathname}${location.hash}`;
      navigate(newUrl, { replace, state: location.state });
    },
    [location, navigate]
  );

  /**
   * Navigate with explicit history state
   *
   * Useful for preserving complex state across navigation.
   *
   * @param url - Target URL
   * @param state - State to attach to history entry
   * @param replace - Whether to replace current history entry (default: false)
   *
   * @example
   * ```typescript
   * // Navigate with scroll position
   * navigateWithState('/organizations', {
   *   scrollY: 450,
   *   filters: { status: 'active' }
   * });
   * ```
   */
  const navigateWithState = useCallback(
    (url: string, state: Record<string, unknown>, replace = false) => {
      navigate(url, { state, replace });
    },
    [navigate]
  );

  return {
    /**
     * Navigate to a route with type-safe parameters
     */
    navigateTo,

    /**
     * Navigate back to previous route
     */
    goBack,

    /**
     * Navigate to return path from location state
     */
    goToReturnPath,

    /**
     * Update query parameters without navigation
     */
    updateQuery,

    /**
     * Clear specific query parameters
     */
    clearQuery,

    /**
     * Clear all query parameters
     */
    clearAllQuery,

    /**
     * Navigate with explicit history state
     */
    navigateWithState,

    /**
     * Current location (from useLocation)
     */
    location,
  };
}

/**
 * Hook to get current return path from location state
 *
 * @param defaultPath - Default path if no return path in state
 * @returns Return path string
 *
 * @example
 * ```typescript
 * function DetailView() {
 *   const returnPath = useReturnPath('/organizations');
 *
 *   return (
 *     <Link to={returnPath}>Back to List</Link>
 *   );
 * }
 * ```
 */
export function useReturnPath(defaultPath: string): string {
  const location = useLocation();
  return (location.state as any)?.returnPath || defaultPath;
}

/**
 * Hook to check if navigation has history
 *
 * @returns True if browser has navigation history
 *
 * @example
 * ```typescript
 * function Header() {
 *   const hasHistory = useHasHistory();
 *
 *   return (
 *     <button disabled={!hasHistory} onClick={() => navigate(-1)}>
 *       Back
 *     </button>
 *   );
 * }
 * ```
 */
export function useHasHistory(): boolean {
  return window.history.length > 1;
}
